import type { Address } from 'viem'
import { getRobinhoodPublicClient } from '@/lib/chain/publicClient'
import { curveLaunchAbi } from '@/lib/launchpad/abi'
import { TOTAL_SUPPLY } from '@/lib/launchpad/constants'
import {
  getV4GraduateAddress,
  isEthAddress,
  isNonZeroAddress,
  LIVE_POOL_MANAGER,
  LIVE_POSITION_MANAGER,
  LIVE_V4_GRADUATE,
  sameAddress,
} from '@/lib/launchpad/contracts'
import { fetchExplorerTokenHolders, fetchExplorerWalletBalance, invalidateExplorerTokenHolders } from '@/lib/explorerHolders'
import { getSimHoldersForLaunch } from '@/lib/launchpad/store'
import type { LaunchState } from '@/lib/launchpad/types'

const ZERO = '0x0000000000000000000000000000000000000000'
export const BURN_ADDRESS = '0x000000000000000000000000000000000000dEaD'
const UNISWAP_ADDRESSES = [
  LIVE_POOL_MANAGER,
  LIVE_POSITION_MANAGER,
  LIVE_V4_GRADUATE,
  getV4GraduateAddress(),
]
const cache = new Map<string, LaunchHolder[]>()
const fetched = new Set<string>()
const inflight = new Map<string, Promise<LaunchHolder[]>>()

export type HolderRole = 'creator' | 'token' | 'uniswap' | 'burn' | 'wallet'

export interface LaunchHolder {
  address: string
  amount: bigint
  percent: number
  isCreator: boolean
  role: HolderRole
}

const ROLE_RANK: Record<HolderRole, number> = {
  creator: 0,
  token: 1,
  uniswap: 2,
  burn: 3,
  wallet: 4,
}

export function holderRole(address: string, creatorAddress?: string, tokenAddress?: string): HolderRole {
  if (sameAddress(address, creatorAddress))
    return 'creator'
  if (sameAddress(address, tokenAddress))
    return 'token'
  if (sameAddress(address, BURN_ADDRESS) || sameAddress(address, ZERO))
    return 'burn'
  if (UNISWAP_ADDRESSES.some(item => sameAddress(address, item)))
    return 'uniswap'
  return 'wallet'
}

function uniqueTokenContracts(launch: LaunchState): Address[] {
  if (isEthAddress(launch.tokenAddress))
    return [launch.tokenAddress]
  if (isEthAddress(launch.contractAddress))
    return [launch.contractAddress]
  return []
}

async function readTokenBalance(token: Address, account: Address): Promise<bigint | undefined> {
  try {
    const amount = await getRobinhoodPublicClient().readContract({
      address: token,
      abi: curveLaunchAbi,
      functionName: 'balanceOf',
      args: [account],
    })
    return typeof amount === 'bigint' ? amount : undefined
  }
  catch {
    return undefined
  }
}

/**
 * Reads a wallet's token balance from the public RPC, cross-checked
 * against a Blockscout-backed read performed server-side by the
 * `tokenHolders` Deno function. The public Robinhood RPC can be slow,
 * rate-limited, or momentarily inconsistent for a specific wallet/call
 * even when it is healthy for other requests, which previously could
 * show "holding 0" in the trade box for a wallet that genuinely holds
 * tokens. Taking the max of both sources (and only trusting one source
 * when the other fails) makes this read as reliable as the holders list,
 * which already moved to Blockckout-only to fix the same class of issue.
 */
export async function readWalletTokenBalance(launch: LaunchState, wallet?: string): Promise<bigint> {
  if (!isEthAddress(wallet))
    return 0n
  const contracts = uniqueTokenContracts(launch)
  if (!contracts.length)
    throw new Error('Token balance read failed')

  const [rpcResults, explorerResults] = await Promise.all([
    Promise.all(contracts.map(token => readTokenBalance(token, wallet))),
    Promise.all(contracts.map(token => fetchExplorerWalletBalance(token, wallet))),
  ])

  const known: bigint[] = []
  for (const amount of rpcResults) {
    if (typeof amount === 'bigint')
      known.push(amount)
  }
  for (const result of explorerResults) {
    if (result && typeof result.tokenAmount === 'bigint')
      known.push(result.tokenAmount)
  }

  if (!known.length)
    throw new Error('Token balance read failed')
  return known.reduce((max, amount) => (amount > max ? amount : max), 0n)
}

/**
 * Reads a wallet's ETH balance from the public RPC, cross-checked against
 * a Blockscout-backed read. See `readWalletTokenBalance` for why: a single
 * flaky RPC read should never permanently show "Wallet 0 ETH" when the
 * wallet actually has a balance.
 */
export async function readWalletEthBalance(wallet?: string): Promise<bigint> {
  if (!isEthAddress(wallet))
    return 0n

  const [rpcAmount, explorerResult] = await Promise.all([
    getRobinhoodPublicClient().getBalance({ address: wallet }).then(
      amount => (typeof amount === 'bigint' && amount > 0n ? amount : 0n),
      () => undefined,
    ),
    fetchExplorerWalletBalance(undefined, wallet),
  ])

  const explorerAmount = explorerResult && typeof explorerResult.ethWei === 'bigint' ? explorerResult.ethWei : undefined

  if (rpcAmount === undefined && explorerAmount === undefined)
    throw new Error('ETH balance read failed')

  const candidates = [rpcAmount, explorerAmount].filter((value): value is bigint => typeof value === 'bigint')
  return candidates.reduce((max, amount) => (amount > max ? amount : max), 0n)
}

function cacheKey(launch: LaunchState) {
  return (launch.contractAddress || launch.tokenAddress || launch.id).toLowerCase()
}

function launchCacheKeys(launch: LaunchState) {
  const keys = new Set<string>()
  for (const value of [launch.contractAddress, launch.tokenAddress, launch.id]) {
    if (value)
      keys.add(value.toLowerCase())
  }
  return keys
}

/** Drop cached holder rows so the next load re-reads live balances. */
export function invalidateLaunchHolders(launch: LaunchState) {
  for (const key of launchCacheKeys(launch)) {
    cache.delete(key)
    fetched.delete(key)
    inflight.delete(key)
  }
  invalidateExplorerTokenHolders(launch.tokenAddress)
  invalidateExplorerTokenHolders(launch.contractAddress)
}

function mergeAmounts(rows: { address: string, amount: bigint }[]) {
  const map = new Map<string, { address: string, amount: bigint }>()
  for (const row of rows) {
    if (!isNonZeroAddress(row.address) || row.amount < 0n)
      continue
    const key = row.address.toLowerCase()
    const existing = map.get(key)
    if (existing) {
      if (row.amount > existing.amount)
        existing.amount = row.amount
      continue
    }
    map.set(key, { address: row.address, amount: row.amount })
  }
  return [...map.values()]
}

export function holderPercent(amount: bigint, circulating: bigint) {
  if (amount <= 0n || circulating <= 0n)
    return 0
  const value = Number((amount * 1_000_000n) / circulating) / 10_000
  if (value === 0)
    return 0.001
  return value
}

export function holderSupply(totalSupply?: bigint) {
  return totalSupply && totalSupply > 0n ? totalSupply : TOTAL_SUPPLY
}

export function toHolderRows(
  rows: { address: string, amount: bigint }[],
  creatorAddress: string | undefined,
  totalSupply = TOTAL_SUPPLY,
  tokenAddress?: string,
): LaunchHolder[] {
  const merged = mergeAmounts(rows).filter(row => row.amount > 0n)
  const supply = holderSupply(totalSupply)

  return merged
    .map((row) => {
      const role = holderRole(row.address, creatorAddress, tokenAddress)
      return {
        address: row.address,
        amount: row.amount,
        percent: holderPercent(row.amount, supply),
        isCreator: role === 'creator',
        role,
      }
    })
    .sort((a, b) => {
      if (a.role !== b.role)
        return ROLE_RANK[a.role] - ROLE_RANK[b.role]
      if (a.amount === b.amount)
        return a.address.localeCompare(b.address)
      return a.amount > b.amount ? -1 : 1
    })
}

function remember(key: string, rows: LaunchHolder[], complete: boolean) {
  cache.set(key, rows)
  if (complete)
    fetched.add(key)
  return rows
}

export function snapshotLaunchHolders(launch: LaunchState): LaunchHolder[] {
  const key = cacheKey(launch)
  const cached = cache.get(key)
  if (cached)
    return cached
  return toHolderRows(getSimHoldersForLaunch(launch), launch.creatorAddress, TOTAL_SUPPLY, launch.tokenAddress)
}

export function hasLoadedLaunchHolders(launch: LaunchState) {
  return fetched.has(cacheKey(launch))
}

async function fetchExplorerRows(contracts: Address[]): Promise<{ rows: { address: string, amount: bigint }[], ok: boolean }> {
  if (!contracts.length)
    return { rows: [], ok: false }
  const results = await Promise.all(contracts.map(token => fetchExplorerTokenHolders(token)))
  const merged: { address: string, amount: bigint }[] = []
  let ok = false
  for (const rows of results) {
    if (rows === undefined)
      continue
    ok = true
    merged.push(...rows)
  }
  return { rows: merged, ok }
}

async function fetchLaunchHolders(launch: LaunchState): Promise<{ rows: LaunchHolder[], complete: boolean }> {
  const simRows = getSimHoldersForLaunch(launch)
  const contracts = uniqueTokenContracts(launch)
  const explorer = await fetchExplorerRows(contracts)
  const raised = launch.realQuoteRaised > 0n || launch.tokensSold > 0n
  const sourceRows = explorer.ok ? [...simRows, ...explorer.rows] : simRows
  const rows = toHolderRows(
    sourceRows,
    launch.creatorAddress,
    TOTAL_SUPPLY,
    launch.tokenAddress,
  )
  const complete = rows.length > 0 || (!raised && explorer.ok)
  return { rows, complete }
}

export async function loadLaunchHolders(
  launch: LaunchState,
  options?: { force?: boolean, wallet?: string },
): Promise<LaunchHolder[]> {
  const key = cacheKey(launch)
  if (options?.force)
    invalidateLaunchHolders(launch)
  if (!options?.force && fetched.has(key)) {
    const cached = cache.get(key) ?? snapshotLaunchHolders(launch)
    const raised = launch.realQuoteRaised > 0n || launch.tokensSold > 0n
    if (cached.length === 0 && raised) {
      fetched.delete(key)
    }
    else {
      return cached
    }
  }
  const pending = inflight.get(key)
  if (pending)
    return pending

  const promise = fetchLaunchHolders(launch)
    .then((result) => {
      const kept = remember(key, result.rows, result.complete)
      inflight.delete(key)
      return kept
    })
    .catch((error) => {
      inflight.delete(key)
      throw error
    })
  inflight.set(key, promise)
  return promise
}
